import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [expandedCard, setExpandedCard] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    product: '',
    quantity: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6sba5cddmmni7ck9aog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          product: '',
          quantity: '',
          message: ''
        });
        setTimeout(() => setFormStatus(''), 3000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const products = [
    {
      title: 'Freeze Dried Fruits',
      description: 'Premium quality mangoes, strawberries, pineapples, bananas, and more',
      image: 'https://public.readdy.ai/ai/img_res/3c1c38827d46ad0a2dcdb59171401281.jpg',
      details: 'Available in 500g, 1kg, 5kg, 10kg bulk packs'
    },
    {
      title: 'Freeze Dried Vegetables',
      description: 'Fresh okra, carrots, bell peppers, corn, and seasonal vegetables',
      image: 'https://readdy.ai/api/search-image?query=collection%20of%20freeze%20dried%20vegetables%20including%20bright%20green%20okra%20orange%20carrot%20slices%20colorful%20bell%20pepper%20pieces%20arranged%20on%20white%20background%20clean%20professional%20food%20photography%20minimal%20simple%20backdrop&width=800&height=600&seq=fdp002&orientation=landscape',
      details: 'Available in 500g, 1kg, 5kg, 10kg bulk packs'
    },
    {
      title: 'Custom Blends',
      description: 'Tailored fruit and vegetable mixes for your specific needs',
      image: 'https://readdy.ai/api/search-image?query=premium%20mixed%20freeze%20dried%20fruits%20and%20vegetables%20in%20elegant%20clear%20packaging%20bags%20arranged%20professionally%20on%20white%20surface%20showing%20colorful%20product%20variety%20clean%20minimal%20background%20professional%20product%20photography&width=800&height=600&seq=fdp003&orientation=landscape',
      details: 'Available in 500g, 1kg, 5kg, 10kg bulk packs'
    }
  ];

  const pricingTiers = [
    {
      range: '500g - 2kg',
      color: 'bg-pink-100',
      image: 'https://readdy.ai/api/search-image?query=small%20package%20of%20freeze%20dried%20strawberries%20in%20clear%20bag%20on%20soft%20pink%20background%20minimal%20clean%20product%20photography%20professional%20lighting%20simple%20backdrop&width=400&height=500&seq=fdp004&orientation=portrait',
      price: 'Starting at ₱850/kg',
      minOrder: 'Minimum 500g'
    },
    {
      range: '3kg - 9kg',
      color: 'bg-green-100',
      image: 'https://readdy.ai/api/search-image?query=medium%20sized%20packages%20of%20freeze%20dried%20mangoes%20on%20soft%20mint%20green%20background%20clean%20minimal%20product%20photography%20professional%20style%20simple%20backdrop&width=400&height=500&seq=fdp005&orientation=portrait',
      price: 'Starting at ₱750/kg',
      minOrder: 'Minimum 3kg'
    },
    {
      range: '10kg - 49kg',
      color: 'bg-gray-100',
      image: 'https://readdy.ai/api/search-image?query=large%20bulk%20packages%20of%20freeze%20dried%20vegetables%20on%20soft%20gray%20background%20wholesale%20packaging%20clean%20minimal%20professional%20product%20photography%20simple%20backdrop&width=400&height=500&seq=fdp006&orientation=portrait',
      price: 'Starting at ₱650/kg',
      minOrder: 'Minimum 10kg',
      popular: true
    },
    {
      range: '50kg+',
      color: 'bg-amber-100',
      image: 'https://readdy.ai/api/search-image?query=extra%20large%20commercial%20bulk%20packages%20of%20freeze%20dried%20products%20on%20warm%20beige%20background%20industrial%20wholesale%20packaging%20clean%20minimal%20professional%20photography%20simple%20backdrop&width=400&height=500&seq=fdp007&orientation=portrait',
      price: 'Starting at ₱550/kg',
      minOrder: 'Minimum 50kg',
      bestValue: true
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/1ae6d47aa9f5caec142554cc0371daf1/04683b963241abfa25e88f4f0de2b164.jpeg" 
                alt="Freeze Dried Philippines Logo" 
                className="w-12 h-12 object-contain"
              />
              <span className="text-xl font-bold text-gray-900">FREEZE DRIED PHILIPPINES</span>
            </div>
            <div className="flex items-center gap-8">
              <a href="#home" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Home</a>
              <a href="#about" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">About</a>
              <a href="#services" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Services</a>
              <Link to="/products" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Products</Link>
              <Link to="/where-to-buy" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Where to Buy</Link>
              <a href="#bulk-orders" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Bulk Orders</a>
              <a href="#pricing" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Pricing</a>
              <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=vibrant%20colorful%20freeze%20dried%20fruits%20berries%20mango%20slices%20vegetable%20chips%20arranged%20artistically%20on%20rustic%20wooden%20surface%20with%20warm%20natural%20lighting%20creating%20rich%20deep%20brown%20tones%20professional%20food%20photography%20style%20atmospheric%20background&width=1920&height=1080&seq=fdp008&orientation=landscape"
            alt="Freeze Dried Products"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="flex items-start justify-between">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="flex -space-x-2">
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white"></div>
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white"></div>
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white"></div>
                </div>
                <span className="text-white text-sm font-medium">Trusted by 500+ Retailers Nationwide</span>
              </div>
              
              <h1 className="text-7xl font-bold text-white mb-4 leading-tight">
                PREMIUM FREEZE DRIED
              </h1>
              <h2 className="text-6xl font-semibold text-white/95 mb-8">
                FRUITS &amp; VEGETABLES
              </h2>
              
              <a 
                href="#services" 
                className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-2xl font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
              >
                VIEW CATALOG
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            
            <div className="text-white text-right max-w-md">
              <p className="text-lg font-medium leading-relaxed">
                100% Natural • No Preservatives • Extended Shelf Life • Bulk Orders Available
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Catalog Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-gray-900 mb-16 text-center">
            OUR PRODUCT RANGE <span className="text-4xl">🍓</span>
          </h2>
          
          <div className="grid grid-cols-3 gap-6">
            {products.map((product, index) => (
              <div
                key={index}
                className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-400 ${
                  expandedCard === index ? 'col-span-2' : 'col-span-1'
                }`}
                onMouseEnter={() => setExpandedCard(index)}
                style={{ height: '500px' }}
              >
                <img 
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">{product.title}</h3>
                  <p className="text-sm opacity-90 mb-3">{product.description}</p>
                  {expandedCard === index && (
                    <p className="text-sm font-medium bg-white/20 backdrop-blur-sm inline-block px-4 py-2 rounded-lg">
                      {product.details}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bulk Orders & Pricing Section */}
      <section id="bulk-orders" className="py-24 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <p className="text-sm font-serif text-gray-600 mb-2">Wholesale Pricing</p>
            <h2 className="text-6xl font-bold text-gray-900 mb-2">COMPETITIVE RATES</h2>
            <h3 className="text-5xl font-serif italic text-gray-700">for Bulk Orders</h3>
          </div>
          
          <div id="pricing" className="grid grid-cols-4 gap-6">
            {pricingTiers.map((tier, index) => (
              <div key={index} className={`${tier.color} rounded-3xl overflow-hidden relative`}>
                {tier.popular && (
                  <div className="absolute top-4 right-4 bg-gray-900 text-white text-xs font-bold px-3 py-1 rounded-full z-10 whitespace-nowrap">
                    MOST POPULAR
                  </div>
                )}
                {tier.bestValue && (
                  <div className="absolute top-4 right-4 bg-amber-600 text-white text-xs font-bold px-3 py-1 rounded-full z-10 whitespace-nowrap">
                    BEST VALUE
                  </div>
                )}
                <div className="h-96 w-full">
                  <img 
                    src={tier.image}
                    alt={tier.range}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="bg-white p-6">
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">{tier.range}</h4>
                  <p className="text-lg font-semibold text-gray-700 mb-1">{tier.price}</p>
                  <p className="text-sm text-gray-600">{tier.minOrder}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gradient-to-b from-amber-900 to-amber-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-20">
            <div className="inline-block bg-purple-400 px-4 py-2 rounded mb-6">
              <span className="text-sm font-medium text-white">// ABOUT US</span>
            </div>
            <h2 className="text-5xl font-bold text-white mb-6 max-w-4xl">
              FREEZE DRIED PHILIPPINES: Your Trusted Partner in Premium Preserved Produce
            </h2>
            <p className="text-lg text-white/85 leading-relaxed max-w-4xl">
              We specialize in freeze-drying the finest fruits and vegetables from the Philippines, preserving their natural flavor, color, and nutritional value. Our state-of-the-art freeze-drying process removes moisture while maintaining the integrity of the produce, resulting in lightweight, shelf-stable products that retain up to 97% of their original nutrients. With over 15 years of experience, we serve retailers, food manufacturers, and distributors across the country with consistent quality and reliable supply.
            </p>
          </div>
          
          <div className="grid grid-cols-3 gap-16 items-center">
            <div className="col-span-1">
              <div className="bg-amber-100 rounded-xl overflow-hidden shadow-lg w-full h-80">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20food%20processing%20facility%20with%20stainless%20steel%20freeze%20drying%20equipment%20clean%20professional%20environment%20quality%20certification%20badges%20displayed%20on%20white%20wall%20industrial%20food%20production%20setting&width=400&height=500&seq=fdp009&orientation=portrait"
                  alt="Our Facility"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            
            <div className="col-span-2">
              <div className="text-white">
                <div className="text-9xl font-bold mb-4">15+</div>
                <p className="text-2xl font-semibold mb-2">Years of Freeze Drying Excellence</p>
                <p className="text-xl text-white/80 mb-8">Serving Retailers Across the Philippines</p>
                <a 
                  href="#contact" 
                  className="inline-block bg-white text-gray-900 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  LEARN MORE ABOUT OUR PROCESS
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">READY TO PLACE YOUR BULK ORDER?</h2>
            <p className="text-xl text-gray-600 mb-8">Get a custom quote within 24 hours</p>
          </div>
          
          <form onSubmit={handleSubmit} className="bg-gray-50 rounded-3xl p-10 shadow-lg" data-readdy-form id="contact_form">
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  placeholder="Juan Dela Cruz"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  placeholder="juan@company.com"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name *</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  placeholder="Your Company"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  placeholder="+63 912 345 6789"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Product Interest *</label>
                <select
                  name="product"
                  value={formData.product}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm cursor-pointer"
                >
                  <option value="">Select a product</option>
                  <option value="Freeze Dried Fruits">Freeze Dried Fruits</option>
                  <option value="Freeze Dried Vegetables">Freeze Dried Vegetables</option>
                  <option value="Custom Blends">Custom Blends</option>
                  <option value="Mixed Products">Mixed Products</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Estimated Quantity *</label>
                <select
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm cursor-pointer"
                >
                  <option value="">Select quantity range</option>
                  <option value="500g - 2kg">500g - 2kg</option>
                  <option value="3kg - 9kg">3kg - 9kg</option>
                  <option value="10kg - 49kg">10kg - 49kg</option>
                  <option value="50kg+">50kg+</option>
                </select>
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm resize-none"
                placeholder="Tell us more about your requirements..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
            </div>
            
            <button
              type="submit"
              disabled={formStatus === 'sending'}
              className="w-full bg-amber-900 text-white py-4 rounded-xl font-semibold hover:bg-amber-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {formStatus === 'sending' ? 'SENDING...' : 'REQUEST QUOTE →'}
            </button>
            
            {formStatus === 'success' && (
              <div className="mt-4 p-4 bg-green-100 text-green-800 rounded-xl text-center font-medium">
                Thank you! We'll get back to you within 24 hours.
              </div>
            )}
            
            {formStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-100 text-red-800 rounded-xl text-center font-medium">
                Something went wrong. Please try again.
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start justify-between mb-12">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/1ae6d47aa9f5caec142554cc0371daf1/04683b963241abfa25e88f4f0de2b164.jpeg" 
                alt="Freeze Dried Philippines Logo" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-xl font-bold text-white">FREEZE DRIED PHILIPPINES</span>
            </div>
            <p className="text-sm text-gray-400">Premium Quality Freeze Dried Products Since 2010</p>
          </div>
          
          <div className="grid grid-cols-4 gap-12 mb-16">
            <div>
              <h4 className="text-white font-semibold mb-4">PRODUCTS</h4>
              <ul className="space-y-2">
                <li><a href="#services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Freeze Dried Fruits</a></li>
                <li><a href="#services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Freeze Dried Vegetables</a></li>
                <li><a href="#services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Custom Blends</a></li>
                <li><a href="#services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Sample Packs</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">SERVICES</h4>
              <ul className="space-y-2">
                <li><a href="#bulk-orders" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Bulk Orders</a></li>
                <li><a href="#contact" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Custom Packaging</a></li>
                <li><a href="#contact" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Private Label</a></li>
                <li><a href="#pricing" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Wholesale Program</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">COMPANY</h4>
              <ul className="space-y-2">
                <li><a href="#about" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">About Us</a></li>
                <li><a href="#about" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Our Process</a></li>
                <li><a href="#about" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Certifications</a></li>
                <li><Link to="/where-to-buy" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Where to Buy</Link></li>
                <li><a href="#contact" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>
            
            <div className="text-right">
              <p className="text-lg font-serif text-white mb-2">© FREEZE DRIED PHILIPPINES 2025</p>
              <p className="text-sm text-gray-400 mb-4">All rights reserved</p>
              <div className="flex items-center justify-end gap-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm underline cursor-pointer">Facebook</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm underline cursor-pointer">Instagram</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm underline cursor-pointer">LinkedIn</a>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-12 items-center pt-12 border-t border-gray-800">
            <div className="rounded-2xl overflow-hidden w-full h-48">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20food%20production%20facility%20interior%20with%20workers%20in%20white%20uniforms%20processing%20freeze%20dried%20products%20clean%20industrial%20environment%20quality%20control%20setting%20on%20white%20background&width=400&height=300&seq=fdp010&orientation=landscape"
                alt="Production Facility"
                className="w-full h-full object-cover object-top"
              />
            </div>
            
            <div className="text-center">
              <p className="text-2xl font-serif text-gray-300 leading-relaxed">
                FRESH QUALITY<br />
                PRESERVED NATURALLY<br />
                DELIVERED NATIONWIDE
              </p>
            </div>
            
            <div>
              <p className="text-sm text-gray-400 mb-4 leading-relaxed">
                Subscribe for wholesale updates and special bulk pricing offers
              </p>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 rounded-xl bg-transparent border border-gray-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                />
                <button className="bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-600 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                  SUBSCRIBE
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}